import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "antd";
import styles from "../../../styles/Components/new-insight/watchlist/WatchlistSummaryBoard.module.css";
import { getWatchlistSummary } from "../../../redux/new-insight/actions";
import useServiceStatus from "../../../hooks/useServiceStatus";
import ServiceActivationModal from "../../../shared/service-activation/ServiceActivationModal";
import WatchlistReplacedModal from "./WatchlistReplacedModal";

function coinLogo(symbol) {
  return `https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/symbol_icons/${symbol}.png`;
}

function WatchlistSummaryBoard() {
  const dispatch = useDispatch();
  const {
    main: { userData },
    newInsightState: {
      watchlistSummary,
      watchlistSummaryStatus,
      watchlistSummaryError,
    },
  } = useSelector((state) => state);
  const { isActive } = useServiceStatus("insight");
  const [showServiceActivationModal, setShowServiceActivationModal] =
    useState(false);
  const [showReplaced, setShowReplaced] = useState(false);
  const replacingRef = useRef(false);

  const summary = watchlistSummary?.summary || "";
  const coins = Array.isArray(watchlistSummary?.suggested_coins)
    ? watchlistSummary.suggested_coins
    : [];
  const visual = watchlistSummary?.visual_summary || {};
  const replacing = watchlistSummaryStatus === "replacing";
  const loading = watchlistSummaryStatus === "loading" && !summary;

  useEffect(() => {
    if (!userData?.email || watchlistSummaryStatus) return;
    dispatch(
      getWatchlistSummary(
        userData["custom:custom_username"],
        userData.email,
        false,
      ),
    );
  }, [dispatch, userData, watchlistSummaryStatus]);

  useEffect(() => {
    if (watchlistSummaryStatus === "replacing") {
      replacingRef.current = true;
      return;
    }
    if (!replacingRef.current) return;
    replacingRef.current = false;
    if (watchlistSummaryStatus === "idle" && watchlistSummary?.replaced && coins.length) {
      setShowReplaced(true);
    }
  }, [coins.length, watchlistSummary, watchlistSummaryStatus]);

  const replaceWatchlist = () => {
    if (!coins.length || replacing) return;
    if (!isActive) {
      setShowServiceActivationModal(true);
      return;
    }
    dispatch(
      getWatchlistSummary(
        userData["custom:custom_username"],
        userData?.email,
        true,
      ),
    );
  };

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Watchlist summary</h3>
      </div>
      {loading ? <p className={styles.placeholder}>Loading summary...</p> : null}
      {watchlistSummaryError ? (
        <p className={styles.error}>
          {typeof watchlistSummaryError === "string"
            ? watchlistSummaryError
            : "Couldn't load the watchlist summary."}
        </p>
      ) : null}
      {summary ? <p className={styles.summary}>{summary}</p> : null}
      {summary ? (
        <div className={styles.counts}>
          <span className={styles.count}>
            {visual.positive_sentiment ?? 0} positive sentiment
          </span>
          <span className={styles.count}>
            {visual.high_geopolitical_risk ?? 0} high geopolitical risk
          </span>
          <span className={styles.count}>
            {visual.weekly_suggested ?? 0} from weekly portfolio
          </span>
        </div>
      ) : null}
      {coins.length > 0 ? (
        <div className={styles.chips}>
          {coins.map((coin) => {
            const weekly = String(coin.reason || "").startsWith("Weekly");
            return (
              <span
                key={`${coin.symbol}-${coin.reason}`}
                className={`${styles.chip} ${weekly ? styles.weekly : styles.sentiment}`}
              >
                <img
                  src={coinLogo(coin.symbol)}
                  alt=""
                  className={styles.logo}
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = "/placeholders/asset-logo.svg";
                  }}
                />
                {coin.symbol}
                <span className={styles.reason}>{coin.reason}</span>
              </span>
            );
          })}
        </div>
      ) : null}
      <Button
        type="primary"
        loading={replacing}
        disabled={!coins.length || loading}
        onClick={replaceWatchlist}
      >
        Replace personalized watchlist
      </Button>
      {showServiceActivationModal && (
        <ServiceActivationModal
          service="insai"
          show={showServiceActivationModal}
          setShow={setShowServiceActivationModal}
        />
      )}
      <WatchlistReplacedModal
        open={showReplaced}
        coins={coins}
        onClose={() => setShowReplaced(false)}
      />
    </section>
  );
}

export default WatchlistSummaryBoard;
